import React from 'react';
import {Anchor, Menu, Text} from '@albert-io/atomic';

import type {AdminSchool} from './useAdminSchools';

const SchoolGroup = ({heading, schools}: {heading: string; schools: AdminSchool[]}) => (
  <div>
    <Text bold size='m'>
      {heading}
    </Text>
    <div className='mt-x1 font-normal'>
      {schools.map((school) => (
        <Text as='div' size='m' key={school.id}>
          {school.name}
        </Text>
      ))}
    </div>
  </div>
);

interface RestrictionMenuProps {
  restricted: AdminSchool[];
  unrestricted: AdminSchool[];
  /** Guide-surface CTA: opens the manage-restrictions modal inline ("Edit Settings"). */
  onEdit?: () => void;
  /**
   * Question-surface CTA: links out to the assessment guide (restrictions are managed there rather
   * than inline) — signalled with the external-link icon. Ignored when `onEdit` is provided.
   */
  assessmentGuideHref?: string;
  /** Invoked after the CTA is actioned so the surrounding popover can close itself. */
  onClose?: () => void;
}

/**
 * Per-school restricted/unrestricted breakdown shown when an admin opens a restriction indicator
 * (DQA #17841). One shared design for both surfaces; only the CTA differs — guide surfaces open the
 * manage-restrictions modal inline (`onEdit`), question surfaces link out to the assessment guide
 * (`assessmentGuideHref`).
 */
export const RestrictionMenu = ({
  restricted,
  unrestricted,
  onEdit,
  assessmentGuideHref,
  onClose
}: RestrictionMenuProps) => (
  <div role='menu' aria-label='Restriction details' className='flex flex-col text-left'>
    {restricted.length > 0 && (
      <Menu.Item readonly>
        <SchoolGroup heading='Content restricted at:' schools={restricted} />
      </Menu.Item>
    )}
    {unrestricted.length > 0 && (
      <Menu.Item readonly>
        <SchoolGroup heading='Unrestricted at:' schools={unrestricted} />
      </Menu.Item>
    )}
    {/* Presentational row so only the link text is clickable, not the whole row (DQA #17841). */}
    {onEdit ? (
      <>
        <Menu.Divider />
        <div role='none' className='px-x1-half py-x1 font-bold'>
          <Anchor
            role='menuitem'
            as='button'
            type='button'
            noUnderline
            onClick={() => {
              onEdit();
              onClose?.();
            }}
          >
            Edit Settings
          </Anchor>
        </div>
      </>
    ) : (
      assessmentGuideHref && (
        <>
          <Menu.Divider />
          <div role='none' className='px-x1-half py-x1 font-bold'>
            <Anchor
              role='menuitem'
              to={assessmentGuideHref}
              noUnderline
              icon='arrow-up-right-from-square'
              iconPosition='right'
              onClick={() => onClose?.()}
            >
              Edit restriction settings in the assessment guide
            </Anchor>
          </div>
        </>
      )
    )}
  </div>
);
